/**
 * MemoryService
 *
 * Manages memories and stories with rich text, audio, and video content.
 * Extends BaseContentService for standardized 10-step integration pattern.
 *
 * Features:
 * - Rich text content with HTML support
 * - Audio file upload and management
 * - Video file upload and management
 * - Draft/publish workflow
 * - Approval workflow integration
 * - SystemConfig validation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  QueryConstraint,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  UploadTaskSnapshot,
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import {
  Memory,
  MemoryCategory,
  MemoryMetadata,
  MemoryFormData,
  MemoryFilters,
  MemoryApprovalStatus,
} from '../types/memory';
import { SystemConfig } from '../types';

import {
  BaseContentService,
  BaseContentEntity,
  CreateContentOptions,
} from './baseContentService';
import { SystemConfigService } from './systemConfigService';
import { ChangeTrackingService } from './changeTrackingService';
import { AuditLogService } from './auditLogService';
import { mentionService } from './mentionService';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Memory entity extending base content entity
 */
export interface MemoryEntity extends BaseContentEntity {
  // Association
  personName: string;

  // Content
  title: string;
  content: string; // Rich text HTML
  excerpt: string; // Plain text excerpt (first 200 chars)

  // Categorization
  category: MemoryCategory;
  tags: string[];

  // Time period
  dateFrom?: string;
  dateTo?: string;
  timeframe?: string;

  // Media attachments
  audioURL?: string;
  audioFileName?: string;
  audioSizeMB?: number;
  audioLengthSeconds?: number;

  videoURL?: string;
  videoFileName?: string;
  videoSizeMB?: number;
  videoLengthSeconds?: number;
  videoThumbnailURL?: string;

  // Authorship
  authorId: string;
  authorName: string;
  narrator?: string;
  narratorId?: string;

  // Publishing
  isDraft: boolean;
  publishedAt?: Date;

  // Moderation
  approvalStatus: MemoryApprovalStatus;
  approvedBy?: string;
  approvedAt?: Date;
  rejectionReason?: string;

  // Engagement
  likeCount: number;
  viewCount: number;
  commentCount: number;

  // Metadata
  lastEditedAt?: Date;
  autoSavedAt?: Date;

  // Soft delete
  deletedBy?: string;
  deletedAt?: Date;
}

/**
 * Memory creation data (before Firebase Storage upload)
 */
interface MemoryCreationData {
  personId: string;
  personName: string;
  title: string;
  content: string;
  excerpt: string;
  category: MemoryCategory;
  tags: string[];
  dateFrom?: string;
  dateTo?: string;
  timeframe?: string;
  audioURL?: string;
  audioFileName?: string;
  audioSizeMB?: number;
  videoURL?: string;
  videoFileName?: string;
  videoSizeMB?: number;
  videoThumbnailURL?: string;
  authorName: string;
  narrator?: string;
  narratorId?: string;
  isDraft: boolean;
  publishedAt?: Date;
}

// ============================================================================
// MemoryService Class
// ============================================================================

/**
 * MemoryService - Manages memory operations
 * Extends BaseContentService for 10-step compliance
 */
class MemoryServiceImpl extends BaseContentService<MemoryEntity> {
  // ============================================================================
  // STATIC CONSTANTS
  // ============================================================================

  private static readonly STORAGE_PATH_PREFIX = 'memories';

  // ============================================================================
  // ABSTRACT PROPERTIES IMPLEMENTATION
  // ============================================================================

  protected collectionName = 'memories';
  protected featureFlagKey: keyof SystemConfig['features'] = 'enableMemoriesTab';
  protected approvalConfigKey: keyof SystemConfig['content'] = 'requireMemoryApproval';
  protected maxPerPersonConfigKey: keyof SystemConfig['content'] = 'maxMemoriesPerPerson';
  protected authorFieldName = 'authorId'; // Memories use authorId as author field
  protected changeTrackingEntityType = 'memory';

  // ============================================================================
  // ABSTRACT METHODS IMPLEMENTATION
  // ============================================================================

  /**
   * Validate memory-specific data
   */
  protected async validateContent(data: MemoryCreationData, config: SystemConfig): Promise<void> {
    // Validate text length
    const maxTextLength = config.content.maxMemoryTextLength || 50000;
    if (data.content.length > maxTextLength) {
      throw new Error(`Memory text exceeds maximum length of ${maxTextLength} characters`);
    }

    // Validate required fields
    if (!data.title || !data.content) {
      throw new Error('title and content are required');
    }

    if (!data.personId) {
      throw new Error('personId is required for memory creation');
    }

    // Note: Audio/video validation happens before upload in saveMemory method
  }

  /**
   * Build Firestore query constraints from filters
   */
  protected buildFilters(filters: MemoryFilters): QueryConstraint[] {
    const constraints: QueryConstraint[] = [];

    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }

    if (filters.isDraft !== undefined) {
      constraints.push(where('isDraft', '==', filters.isDraft));
    }

    if (filters.approvalStatus) {
      constraints.push(where('approvalStatus', '==', filters.approvalStatus));
    }

    if (filters.authorId) {
      constraints.push(where('authorId', '==', filters.authorId));
    }

    return constraints;
  }

  /**
   * Map Firestore document data to typed entity
   */
  protected mapDocumentToEntity(docData: any, docId: string): MemoryEntity {
    return {
      id: docId,
      personId: docData.personId,
      personName: docData.personName,
      title: docData.title,
      content: docData.content,
      excerpt: docData.excerpt,
      category: docData.category,
      tags: docData.tags || [],
      dateFrom: docData.dateFrom,
      dateTo: docData.dateTo,
      timeframe: docData.timeframe,
      audioURL: docData.audioURL,
      audioFileName: docData.audioFileName,
      audioSizeMB: docData.audioSizeMB,
      audioLengthSeconds: docData.audioLengthSeconds,
      videoURL: docData.videoURL,
      videoFileName: docData.videoFileName,
      videoSizeMB: docData.videoSizeMB,
      videoLengthSeconds: docData.videoLengthSeconds,
      videoThumbnailURL: docData.videoThumbnailURL,
      authorId: docData.authorId,
      authorName: docData.authorName,
      narrator: docData.narrator,
      narratorId: docData.narratorId,
      isDraft: docData.isDraft,
      publishedAt: docData.publishedAt ? this.timestampToDate(docData.publishedAt) : undefined,
      approvalStatus: docData.approvalStatus,
      approvedBy: docData.approvedBy,
      approvedAt: docData.approvedAt ? this.timestampToDate(docData.approvedAt) : undefined,
      rejectionReason: docData.rejectionReason,
      likeCount: docData.likeCount || 0,
      viewCount: docData.viewCount || 0,
      commentCount: docData.commentCount || 0,
      createdAt: this.timestampToDate(docData.createdAt),
      updatedAt: this.timestampToDate(docData.updatedAt),
      lastEditedAt: docData.lastEditedAt ? this.timestampToDate(docData.lastEditedAt) : undefined,
      autoSavedAt: docData.autoSavedAt ? this.timestampToDate(docData.autoSavedAt) : undefined,
      createdBy: docData.createdBy || docData.authorId, // Fallback for old data
      isApproved: docData.isApproved,
      isDeleted: docData.isDeleted,
      deletedBy: docData.deletedBy,
      deletedAt: docData.deletedAt ? this.timestampToDate(docData.deletedAt) : undefined,
    };
  }

  // ============================================================================
  // PUBLIC API METHODS
  // ============================================================================

  /**
   * Create or update a memory (draft or published)
   * Handles text content, audio, and video uploads
   * Follows complete 10-step integration pattern
   */
  async saveMemory(
    formData: MemoryFormData,
    userId: string,
    userName: string,
    userRole: string,
    existingMemoryId?: string
  ): Promise<Memory> {
    try {
      // Get config for validation
      const config = await SystemConfigService.getConfig();

      // Validate audio file if provided
      if (formData.audioFile) {
        await this.validateAudioFile(formData.audioFile, config);
      }

      // Validate video file if provided
      if (formData.videoFile) {
        await this.validateVideoFile(formData.videoFile, config);
      }

      // Generate memory ID for storage paths
      const memoryId = existingMemoryId || this.generateId();

      // Upload audio if provided
      let audioURL: string | undefined;
      let audioFileName: string | undefined;
      let audioSizeMB: number | undefined;

      if (formData.audioFile) {
        const audioData = await this.uploadAudio(
          formData.audioFile,
          formData.personId,
          memoryId
        );
        audioURL = audioData.url;
        audioFileName = audioData.fileName;
        audioSizeMB = audioData.sizeMB;
      }

      // Upload video if provided
      let videoURL: string | undefined;
      let videoFileName: string | undefined;
      let videoSizeMB: number | undefined;
      let videoThumbnailURL: string | undefined;

      if (formData.videoFile) {
        const videoData = await this.uploadVideo(
          formData.videoFile,
          formData.personId,
          memoryId
        );
        videoURL = videoData.url;
        videoFileName = videoData.fileName;
        videoSizeMB = videoData.sizeMB;
        videoThumbnailURL = videoData.thumbnailURL;
      }

      // Generate excerpt (first 200 characters)
      const plainText = this.stripHtmlTags(formData.content);
      const excerpt = plainText.substring(0, 200) + (plainText.length > 200 ? '...' : '');

      // Prepare memory data
      const now = new Date();
      const memoryData: MemoryCreationData = {
        personId: formData.personId,
        personName: '', // Will be populated by UI
        title: formData.title,
        content: formData.content,
        excerpt,
        category: formData.category,
        tags: formData.tags || [],
        dateFrom: formData.dateFrom,
        dateTo: formData.dateTo,
        timeframe: formData.timeframe,
        audioURL,
        audioFileName,
        audioSizeMB,
        videoURL,
        videoFileName,
        videoSizeMB,
        videoThumbnailURL,
        authorName: userName,
        narrator: formData.narrator,
        narratorId: formData.narratorId,
        isDraft: formData.isDraft,
        publishedAt: formData.isDraft ? undefined : now,
      };

      let createdEntity: MemoryEntity;

      if (existingMemoryId) {
        // Update existing memory - use direct Firestore update
        const docRef = doc(db, this.collectionName, existingMemoryId);
        const existingDoc = await getDoc(docRef);

        if (!existingDoc.exists()) {
          throw new Error('Memory not found');
        }

        const existingData = existingDoc.data();
        const beforeState = this.mapDocumentToEntity(existingData, existingMemoryId);

        // Check permissions
        if (!this.checkOwnerOrAdmin(beforeState, userId, userRole)) {
          throw new Error('You do not have permission to update this memory');
        }

        // Update document
        const updateData = this.removeUndefinedFields({
          ...memoryData,
          updatedAt: serverTimestamp(),
          lastEditedAt: serverTimestamp(),
        });

        const { updateDoc } = await import('firebase/firestore');
        await updateDoc(docRef, updateData);

        // Fetch updated document
        const updatedDoc = await getDoc(docRef);
        createdEntity = this.mapDocumentToEntity(updatedDoc.data()!, updatedDoc.id);

        // Log change
        await ChangeTrackingService.logChange(
          {
            entityType: 'memory',
            entityId: existingMemoryId,
            changeType: 'update',
            beforeState: beforeState,
            afterState: createdEntity,
          },
          userId,
          userName,
          true
        );

      } else {
        // Create new memory using BaseContentService
        createdEntity = await this.executeCreatePattern(
          memoryData,
          userId,
          userName,
          userRole,
          {
            requiresApproval: !formData.isDraft, // Drafts don't need approval
            entityTypeName: 'memory',
            skipNotification: formData.isDraft, // Don't notify for drafts
          }
        );
      }

      // Convert to Memory type with additional fields
      const memory: Memory = {
        ...createdEntity,
        likeCount: 0,
        viewCount: 0,
        commentCount: 0,
      } as unknown as Memory;

      // Send mention notifications
      if (formData.mentions && formData.mentions.length > 0) {
        await mentionService.processMentions(formData.mentions, {
          contentType: 'memory',
          contentId: memory.id!,
          authorId: userId,
          authorName: userName,
          contentPreview: formData.content.substring(0, 100),
          targetType: 'person',
          targetId: formData.personId,
        });
      }

      return memory;

    } catch (error: any) {
      console.error('Error saving memory:', error);
      throw new Error(`Failed to save memory: ${error.message}`);
    }
  }

  /**
   * Get all memories for a specific person
   */
  async getMemoriesByPerson(
    personId: string,
    filters?: MemoryFilters,
    userId?: string
  ): Promise<Memory[]> {
    try {
      const entities = await this.getByPerson(personId, filters, userId);

      // Filter out drafts unless they belong to the current user
      const filteredEntities = entities.filter((entity) => {
        if (entity.isDraft && entity.authorId !== userId) {
          return false;
        }
        return true;
      });

      return filteredEntities as unknown as Memory[];
    } catch (error: any) {
      console.error('Error getting memories:', error);
      throw new Error(`Failed to get memories: ${error.message}`);
    }
  }

  /**
   * Get a specific memory by ID
   */
  async getMemoryById(memoryId: string, userId?: string): Promise<Memory | null> {
    try {
      const entity = await this.getById(memoryId, userId);
      if (!entity) {
        return null;
      }

      // Check if user has permission to view draft
      if (entity.isDraft && entity.authorId !== userId) {
        return null;
      }

      return entity as unknown as Memory;
    } catch (error: any) {
      console.error('Error getting memory:', error);
      throw new Error(`Failed to get memory: ${error.message}`);
    }
  }

  /**
   * Publish a draft memory
   */
  async publishMemory(
    memoryId: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, memoryId);
      const existingDoc = await getDoc(docRef);

      if (!existingDoc.exists()) {
        throw new Error('Memory not found');
      }

      const existingMemory = this.mapDocumentToEntity(existingDoc.data(), existingDoc.id);

      // Check permissions
      if (!this.checkOwnerOrAdmin(existingMemory, userId, userRole)) {
        throw new Error('You do not have permission to publish this memory');
      }

      // Get approval status
      const config = await SystemConfigService.getConfig();
      const requiresApproval = config.content.requireMemoryApproval;
      const isAdmin = userRole === 'admin';
      const approvalStatus: MemoryApprovalStatus =
        isAdmin || !requiresApproval ? 'approved' : 'pending';

      // Update memory
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        isDraft: false,
        publishedAt: serverTimestamp(),
        approvalStatus,
        isApproved: approvalStatus === 'approved',
        approvedBy: isAdmin && approvalStatus === 'approved' ? userId : undefined,
        approvedAt: isAdmin && approvalStatus === 'approved' ? serverTimestamp() : undefined,
        updatedAt: serverTimestamp(),
      });

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'memory',
          entityId: memoryId,
          changeType: 'update',
          beforeState: existingMemory,
          afterState: { ...existingMemory, isDraft: false },
        },
        userId,
        userName,
        true
      );

    } catch (error: any) {
      console.error('Error publishing memory:', error);
      throw new Error(`Failed to publish memory: ${error.message}`);
    }
  }

  /**
   * Soft delete a memory
   */
  async deleteMemory(
    memoryId: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      // Use BaseContentService soft delete (handles change tracking and audit logging)
      await this.softDelete(memoryId, userId, userName, userRole);
    } catch (error: any) {
      console.error('Error deleting memory:', error);
      throw new Error(`Failed to delete memory: ${error.message}`);
    }
  }

  /**
   * Approve a pending memory (admin only)
   */
  async approveMemory(
    memoryId: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      // Use BaseContentService approve method
      await this.approve(memoryId, userId, userName, 'admin');

      // Update approval status field (Memory-specific)
      const docRef = doc(db, this.collectionName, memoryId);
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        approvalStatus: 'approved',
        updatedAt: serverTimestamp(),
      });

    } catch (error: any) {
      console.error('Error approving memory:', error);
      throw new Error(`Failed to approve memory: ${error.message}`);
    }
  }

  /**
   * Reject a pending memory (admin only)
   */
  async rejectMemory(
    memoryId: string,
    reason: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      // Use BaseContentService reject method
      await this.reject(memoryId, reason, userId, userName, 'admin');

      // Update approval status field (Memory-specific)
      const docRef = doc(db, this.collectionName, memoryId);
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        approvalStatus: 'rejected',
        updatedAt: serverTimestamp(),
      });

    } catch (error: any) {
      console.error('Error rejecting memory:', error);
      throw new Error(`Failed to reject memory: ${error.message}`);
    }
  }

  /**
   * Get all memories awaiting approval (admin only)
   */
  async getMemoriesAwaitingApproval(limit?: number): Promise<Memory[]> {
    try {
      // Get unapproved memories using base method
      const entities = await this.getAwaitingApproval(limit || 50);

      // Filter out drafts (drafts shouldn't be in approval queue)
      const publishedEntities = entities.filter(entity => !entity.isDraft);

      return publishedEntities as unknown as Memory[];
    } catch (error: any) {
      console.error('Error getting pending memories:', error);
      throw new Error(`Failed to get pending memories: ${error.message}`);
    }
  }

  /**
   * Increment view count
   */
  async incrementViewCount(memoryId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, memoryId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const currentCount = docSnap.data().viewCount || 0;
        const { updateDoc } = await import('firebase/firestore');
        await updateDoc(docRef, {
          viewCount: currentCount + 1,
        });
      }
    } catch (error: any) {
      console.error('Error incrementing view count:', error);
      // Don't throw - this is non-critical
    }
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Validate audio file
   */
  private async validateAudioFile(file: File, config: SystemConfig): Promise<void> {
    const maxSizeMB = config.content.maxMemoryAudioSizeMB || 50;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      throw new Error(`Audio file size exceeds maximum allowed size of ${maxSizeMB}MB`);
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    const allowedTypes = config.content.allowedMemoryAudioTypes || ['mp3', 'm4a', 'wav'];

    if (!allowedTypes.includes(fileExtension)) {
      throw new Error(`Audio type .${fileExtension} is not allowed. Allowed types: ${allowedTypes.join(', ')}`);
    }
  }

  /**
   * Validate video file
   */
  private async validateVideoFile(file: File, config: SystemConfig): Promise<void> {
    const maxSizeMB = config.content.maxMemoryVideoSizeMB || 200;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      throw new Error(`Video file size exceeds maximum allowed size of ${maxSizeMB}MB`);
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    const allowedTypes = config.content.allowedMemoryVideoTypes || ['mp4', 'mov'];

    if (!allowedTypes.includes(fileExtension)) {
      throw new Error(`Video type .${fileExtension} is not allowed. Allowed types: ${allowedTypes.join(', ')}`);
    }
  }

  /**
   * Upload audio file to Firebase Storage
   */
  private async uploadAudio(
    file: File,
    personId: string,
    memoryId: string
  ): Promise<{ url: string; fileName: string; sizeMB: number }> {
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop() || 'mp3';
    const fileName = `${timestamp}.${fileExtension}`;
    const storagePath = `${MemoryServiceImpl.STORAGE_PATH_PREFIX}/${personId}/${memoryId}/audio/${fileName}`;
    const storageRef = ref(storage, storagePath);

    const uploadTask = uploadBytesResumable(storageRef, file);
    const snapshot = await uploadTask;
    const url = await getDownloadURL(snapshot.ref);

    return {
      url,
      fileName,
      sizeMB: Math.round((file.size / (1024 * 1024)) * 100) / 100,
    };
  }

  /**
   * Upload video file to Firebase Storage
   */
  private async uploadVideo(
    file: File,
    personId: string,
    memoryId: string
  ): Promise<{ url: string; fileName: string; sizeMB: number; thumbnailURL?: string }> {
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop() || 'mp4';
    const fileName = `${timestamp}.${fileExtension}`;
    const storagePath = `${MemoryServiceImpl.STORAGE_PATH_PREFIX}/${personId}/${memoryId}/video/${fileName}`;
    const storageRef = ref(storage, storagePath);

    const uploadTask = uploadBytesResumable(storageRef, file);
    const snapshot = await uploadTask;
    const url = await getDownloadURL(snapshot.ref);

    // TODO: Generate video thumbnail
    const thumbnailURL = undefined;

    return {
      url,
      fileName,
      sizeMB: Math.round((file.size / (1024 * 1024)) * 100) / 100,
      thumbnailURL,
    };
  }

  /**
   * Generate a unique ID
   */
  private generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substring(2)}`;
  }

  /**
   * Strip HTML tags from text
   */
  private stripHtmlTags(html: string): string {
    if (typeof document !== 'undefined') {
      const tmp = document.createElement('DIV');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    }
    // Server-side fallback (basic regex)
    return html.replace(/<[^>]*>/g, '');
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

/**
 * Singleton instance of MemoryService
 */
export const MemoryService = new MemoryServiceImpl();
